import { useQuery } from '@tanstack/react-query'
import type {
  TelephonyAccountView,
  TelephonyLeaseView,
  TelephonyPoolView,
  TelephonyProfileView,
} from '@/shared/api/__generated__'
import {
  listLineLeasesOptions,
  listSipAccountsOptions,
  listTelephonyPoolsOptions,
  listTelephonyProfilesOptions,
} from './queries'

export interface TelephonyQueryState {
  isPending: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

interface AccountFilter {
  poolId?: string
  profileId?: string
}

// Пул сужает выборку на сервере (?pool=), профиля в query нет — по нему
// фильтруем на клиенте поверх уже полученного списка.
export function useSipAccounts(
  filter: AccountFilter = {},
): TelephonyQueryState & { accounts: TelephonyAccountView[] } {
  const query = useQuery(
    listSipAccountsOptions(filter.poolId != null ? { query: { pool: filter.poolId } } : {}),
  )
  const all = query.data?.data ?? []
  return {
    accounts: all.filter(
      (a) => filter.profileId == null || a.provider_profile_id === filter.profileId,
    ),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useSipAccount(
  accountId: string,
): TelephonyQueryState & { account: TelephonyAccountView | undefined } {
  const { accounts, ...state } = useSipAccounts()
  return { ...state, account: accounts.find((a) => a.id === accountId) }
}

interface LeaseFilter {
  unitId?: string
  accountId?: string
  poolId?: string
}

// Освобождённые аренды в интерфейс не попадают: аренда живёт, пока держит линию, а
// released — след в журнале бэкенда, не состояние телефонии. Отсев здесь один на все
// экраны (страница аренд, карточки юнита, учётки и пула). Юнит сужает выборку на
// сервере (?unit=); учётка и пул query-параметров не имеют — фильтруем на клиенте.
export function useLineLeases(
  filter: LeaseFilter = {},
): TelephonyQueryState & { leases: TelephonyLeaseView[] } {
  const query = useQuery(
    listLineLeasesOptions(filter.unitId != null ? { query: { unit: filter.unitId } } : {}),
  )
  const all = query.data?.data ?? []
  return {
    leases: all.filter(
      (l) =>
        l.status !== 'released' &&
        (filter.accountId == null || l.sip_account_id === filter.accountId) &&
        (filter.poolId == null || l.pool_id === filter.poolId),
    ),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useTelephonyPools(): TelephonyQueryState & { pools: TelephonyPoolView[] } {
  const query = useQuery(listTelephonyPoolsOptions())
  return {
    pools: query.data?.data ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useTelephonyPool(
  poolId: string,
): TelephonyQueryState & { pool: TelephonyPoolView | undefined } {
  const { pools, ...state } = useTelephonyPools()
  return { ...state, pool: pools.find((p) => p.id === poolId) }
}

export function useTelephonyProfiles(): TelephonyQueryState & {
  profiles: TelephonyProfileView[]
} {
  const query = useQuery(listTelephonyProfilesOptions())
  return {
    profiles: query.data?.data ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useTelephonyProfile(
  profileId: string,
): TelephonyQueryState & { profile: TelephonyProfileView | undefined } {
  const { profiles, ...state } = useTelephonyProfiles()
  return { ...state, profile: profiles.find((p) => p.id === profileId) }
}
